import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Sparkles, Car, RotateCcw, Lock } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useDemo } from "@/contexts/DemoContext";
import { useToast } from "@/hooks/use-toast";
import { AiResultCards, type AiAction } from "./ai/AiResultCards";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useMaintenance } from "@/hooks/useMaintenance";
import { useBookings } from "@/hooks/useBookings";
import { useBranchContext } from "@/contexts/BranchContext";
import { useBranches } from "@/hooks/useBranches";
import {
  ASSISTANT_QUICK_PRESET_IDS,
  getAssistantPreset,
  normalizeAssistantPresetQuery,
  type AssistantPresetId,
} from "@/lib/assistantQueryPresets";

interface AiAssistantSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export interface RevenueReportSummary {
  total_revenue: number;
  total_collected: number;
  total_pending: number;
  collection_rate: number;
  total_bookings: number;
  avg_daily_rate: number;
  deposits_held: number;
}

export interface AssistantVehicleResult {
  id: string;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  color?: string | null;
  plate_number?: string | null;
  daily_rate?: number | null;
  category?: string | null;
  status?: string | null;
  image_url?: string | null;
  booking_id?: string;
  booking_end?: string | null;
  customer_name?: string | null;
  current_customer?: string | null;
  current_customer_name?: string | null;
  available_from?: string | null;
}

export interface AssistantScheduleEntry {
  booking_id: string;
  customer_name?: string | null;
  status: string;
  start_date: string;
  end_date: string;
  daily_rate?: number | null;
}

export interface AssistantBookingStat {
  vehicle_id: string;
  make?: string | null;
  model?: string | null;
  plate_number?: string | null;
  image_url?: string | null;
  total_revenue: number;
  rental_count: number;
}

export interface AssistantBookingSummary {
  id: string;
  customer_name?: string | null;
  status?: string | null;
  vehicle_name?: string | null;
  vehicle_plate?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  total_price?: number | null;
}

export interface AssistantCreatedBooking {
  vehicle_name?: string | null;
  vehicle_plate?: string | null;
  customer_name?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  total_price?: number | null;
  days?: number | null;
  daily_rate?: number | null;
}

export interface AssistantBriefing {
  fleet_status?: Record<string, number>;
  pickups_today?: number;
  returns_today?: number;
  overdue_count?: number;
  revenue_recent?: number;
  sections?: { icon?: string; title: string; items: string[] }[];
}

export interface AssistantCustomerProfile {
  id?: string;
  full_name?: string | null;
  phone?: string | null;
  email?: string | null;
  total_bookings?: number;
  total_spend?: number;
  total_paid?: number;
  outstanding?: number;
  active_rental?: { vehicle_name?: string | null; vehicle_plate?: string | null; start_date?: string | null; end_date?: string | null };
  recent_bookings?: { vehicle?: string | null; status?: string | null; total?: number | null }[];
}

export interface AssistantIssue {
  type?: string;
  severity?: string;
  title?: string;
  detail?: string;
  booking_id?: string;
  vehicle_id?: string;
}

export interface AssistantModification {
  customer_name?: string | null;
  vehicle_name?: string | null;
  old_end_date?: string | null;
  new_end_date?: string | null;
  new_total?: number | null;
  days?: number | null;
}

export interface AssistantCancellation {
  customer_name?: string | null;
  vehicle_name?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  reason?: string | null;
}

export interface AssistantUtilization {
  avg_utilization?: number;
  idle_count?: number;
  vehicles?: { vehicle?: string | null; utilization_pct: number; revenue?: number | null }[];
}

export interface AssistantInsights {
  total_customers?: number;
  repeat_customers?: number;
  new_customers?: number;
  top_customers?: { name?: string | null; bookings: number; revenue?: number | null }[];
}

export interface AssistantMaintenanceJob {
  id?: string;
  severity?: string;
  title?: string | null;
  type?: string | null;
  vehicle?: string | null;
  plate?: string | null;
  vendor?: string | null;
  status?: string | null;
  cost?: number | null;
}

export interface SearchResult {
  result_type: "availability" | "status_summary" | "vehicle_list" | "vehicle_detail" | "schedule" | "text" | "returning_soon" | "next_available" | "booking_stats" | "table" | "booking_created" | "daily_briefing" | "customer_360" | "revenue_report" | "attention_needed" | "booking_modified" | "booking_cancelled" | "utilization_report" | "customer_insights" | "booking_list" | "maintenance_list" | "expense_report" | "profit_report" | "revenue_forecast" | "cash_flow" | "action_queue" | "gap_analysis" | "churn_risk" | "upcoming_schedule" | "clarification";
  summary: string;
  parsed_filters?: { start_at: string; end_at: string; category?: string; max_daily_rate?: number; min_daily_rate?: number };
  time_window?: { from_at: string; to_at: string; granularity: "date" | "datetime"; timezone: string; label?: string; mode?: "range" | "anchor" };
  vehicles: AssistantVehicleResult[];
  status_counts?: Record<string, number>;
  total?: number;
  status_filter?: string;
  schedule?: AssistantScheduleEntry[];
  stats?: AssistantBookingStat[];
  metric?: string;
  table?: { headers: string[]; rows: string[][] };
  table_meta?: { type: string; start_date: string; end_date: string; category?: string | null; min_daily_rate?: number | null; max_daily_rate?: number | null };
  booking?: AssistantCreatedBooking;
  briefing?: AssistantBriefing;
  customer_profile?: AssistantCustomerProfile;
  revenue?: number | RevenueReportSummary;
  issues?: AssistantIssue[];
  modification?: AssistantModification;
  cancellation?: AssistantCancellation;
  utilization?: AssistantUtilization;
  insights?: AssistantInsights;
  bookings?: AssistantBookingSummary[];
  availability_snippet?: { headers: string[]; rows: string[][] };
  // maintenance_list fields
  jobs?: AssistantMaintenanceJob[];
  total_cost?: number;
  by_status?: Record<string, number>;
  // expense_report fields
  expenses?: number;
  net_profit?: number;
  expense_breakdown?: { type: string; cost: number; status: string }[];
  period?: { from: string; to: string };
  // new intelligence tools
  profit_items?: { vehicle: string; plate: string; revenue: number; maintenance_cost: number; net_profit: number; margin: number }[];
  forecast_weeks?: { week: string; confirmed: number; revenue: number }[];
  cash_flow?: { deposits_held: number; outstanding_owed: number; scheduled_costs: number; net_position: number };
  action_items?: { priority: number; type: string; urgency: string; title: string; detail: string; booking_id?: string; vehicle_id?: string }[];
  gap_items?: { vehicle: string; plate: string; idle_from: string; idle_to: string; days: number; last_customer?: string }[];
  churn_items?: { name: string; last_booking: string; days_inactive: number; total_bookings: number }[];
  // upcoming_schedule fields
  schedule_days?: {
    date: string;
    label: string;
    events: {
      type: "pickup" | "return" | "maintenance" | "insurance_expiry";
      title: string;
      subtitle: string;
      urgency: "high" | "medium" | "low";
      booking_id?: string;
      vehicle_id?: string;
      maintenance_id?: string;
    }[];
  }[];
  lookahead_days?: number;
  total_events?: number;
  clarification?: {
    question: string;
    options: {
      label: string;
      query: string;
      intent: string;
    }[];
  };
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  result?: SearchResult;
  timestamp: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasAssistantError(value: unknown): value is { error: string } {
  return isRecord(value) && typeof value.error === "string";
}

function isSearchResult(value: unknown): value is SearchResult {
  return (
    isRecord(value) &&
    typeof value.result_type === "string" &&
    typeof value.summary === "string" &&
    Array.isArray(value.vehicles)
  );
}

const QUICK_CHIPS = [
  ...ASSISTANT_QUICK_PRESET_IDS.map((presetId) => getAssistantPreset(presetId)),
];

interface AssistantSearchOptions {
  presetId?: AssistantPresetId;
}

export function AiAssistantSheet({ open, onOpenChange }: AiAssistantSheetProps) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isDemo } = useDemo();
  const navigate = useNavigate();
  const { hasFeature } = useEntitlements();
  const aiEnabled = hasFeature("ai_assistant");
  const { toast } = useToast();
  const { records: maintRecords } = useMaintenance();
  const { bookings } = useBookings();
  const { activeBranchId } = useBranchContext();
  const { branches } = useBranches();
  const activeBranch = branches.find((branch) => branch.id === activeBranchId) ?? null;

  const proactiveChips = useMemo(() => {
    const chips: { label: string; presetId: AssistantPresetId }[] = [];
    const reviewJobs = maintRecords.filter((m) => m.status === "review");
    if (reviewJobs.length > 0)
      chips.push({
        label: `${reviewJobs.length} job${reviewJobs.length > 1 ? "s" : ""} need review`,
        presetId: "maintenanceReview",
      });
    const pendingCost = maintRecords
      .filter((m) => m.cost && m.cost > 0 && !["closed", "rejected"].includes(m.status ?? ""))
      .reduce((s, m) => s + (m.cost || 0), 0);
    if (pendingCost > 0)
      chips.push({
        label: `$${pendingCost.toLocaleString()} pending expenses`,
        presetId: "expenseReport",
      });
    const today = new Date().toISOString().slice(0, 10);
    const overdueReturns = bookings.filter((b) => b.status === "active" && b.end_date < today);
    if (overdueReturns.length > 0)
      chips.push({
        label: `${overdueReturns.length} overdue return${overdueReturns.length > 1 ? "s" : ""}`,
        presetId: "overdueReturns",
      });
    const todayHandovers = bookings.filter((b) => b.status === "ready" && b.start_date === today);
    if (todayHandovers.length > 0)
      chips.push({
        label: `${todayHandovers.length} handover${todayHandovers.length > 1 ? "s" : ""} today`,
        presetId: "actionList",
      });
    return chips;
  }, [maintRecords, bookings]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSearch = useCallback(async (searchQuery: string, options?: AssistantSearchOptions) => {
    if (!searchQuery.trim()) return;
    const preset = options?.presetId ? getAssistantPreset(options.presetId) : null;
    const routedQuery = preset ? searchQuery.trim() || preset.query : normalizeAssistantPresetQuery(searchQuery);
    const userQuery = preset ? searchQuery.trim() || preset.query : searchQuery;

    const userMessage: ChatMessage = { role: "user", content: userQuery, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setError(null);

    try {
      // Build conversation history (text only, no result objects)
      const history = messages.map(m => ({ role: m.role, content: m.content }));

      const { data, error: fnError } = await supabase.functions.invoke("ai-availability", {
        body: {
          query: routedQuery,
          preset_id: preset?.id,
          demo: isDemo || undefined,
          messages: history.length > 0 ? history : undefined,
          parsing_context: {
            current_timestamp: new Date().toISOString(),
            active_branch_id: activeBranchId,
            timezone: activeBranch?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
            locale: typeof navigator !== "undefined" ? navigator.language : "en-US",
            country: activeBranch?.country || null,
          },
        },
      });

      if (fnError) {
        setError(fnError.message || "Failed to search.");
        return;
      }
      const payload: unknown = data;
      if (hasAssistantError(payload)) {
        if (payload.error.includes("Rate limit")) toast({ title: "Rate limited", description: "Please wait before sending another request.", variant: "destructive" });
        else if (payload.error.includes("credits")) toast({ title: "AI credits exhausted", description: "Your AI credits have been used up.", variant: "destructive" });
        setError(payload.error);
        return;
      }
      if (!isSearchResult(payload)) {
        setError("Unexpected AI response. Please try again.");
        return;
      }

      const result = payload;
      const assistantMessage: ChatMessage = {
        role: "assistant",
        content: result.summary,
        result,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [messages, isDemo, activeBranchId, activeBranch?.timezone, activeBranch?.country, toast]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(query);
    setQuery("");
  };

  const handleAction = useCallback((action: AiAction) => {
    if (action.type === "book") {
      onOpenChange(false);
      navigate(action.query); // query contains the full path with params
      return;
    }
    if (action.presetId) {
      const preset = getAssistantPreset(action.presetId);
      handleSearch(action.query || preset.query, { presetId: action.presetId });
      return;
    }
    handleSearch(action.query);
  }, [handleSearch, onOpenChange, navigate]);

  const handleNewConversation = () => {
    setMessages([]);
    setError(null);
    setQuery("");
  };

  const hasMessages = messages.length > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-2">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="h-5 w-5 text-primary" />
              Fleet Assistant
            </SheetTitle>
            {hasMessages && (
              <Button variant="ghost" size="sm" onClick={handleNewConversation} className="text-xs gap-1">
                <RotateCcw className="h-3 w-3" /> New chat
              </Button>
            )}
          </div>
        </SheetHeader>

        {!aiEnabled ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Lock className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">AI Assistant Add-On</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Enable the AI Assistant add-on in your plan settings to ask questions about your fleet in natural language.
            </p>
            <Button variant="default" onClick={() => { onOpenChange(false); navigate("/settings?tab=plan"); }}>
              Go to Plan Settings
            </Button>
          </div>
        ) : (
        <>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 pb-4">
          {/* Empty state */}
          {!hasMessages && !loading && !error && (
            <div className="space-y-4 mt-2">
              <p className="text-sm text-muted-foreground">Ask about your fleet in plain language.</p>

              {proactiveChips.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Right now</p>
                  <div className="flex flex-col gap-1.5">
                    {proactiveChips.map((chip) => (
                      <button key={chip.label}
                        onClick={() => handleSearch(getAssistantPreset(chip.presetId).query, { presetId: chip.presetId })}
                        className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium text-foreground text-left transition-colors hover:bg-accent">
                        {chip.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Quick actions</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_CHIPS.map((chip) => (
                    <button
                      key={chip.id}
                      onClick={() => {
                        setQuery("");
                        handleSearch(chip.query, { presetId: chip.id as AssistantPresetId });
                      }}
                      className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-dashed border-border p-4 text-center">
                <Car className="mx-auto h-8 w-8 text-muted-foreground/50" />
                <p className="mt-2 text-sm text-muted-foreground">
                  Try: "Any car below $50 this weekend?", "What's our net profit this month?", or "Show jobs needing review"
                </p>
              </div>
            </div>
          )}

          {/* Chat messages */}
          {hasMessages && (
            <div className="space-y-4 mt-2">
              {messages.map((msg, i) => (
                <div key={i}>
                  {msg.role === "user" ? (
                    <div className="flex flex-col items-end">
                      <div className="rounded-2xl rounded-br-md bg-primary text-primary-foreground px-4 py-2 max-w-[85%]">
                        <p className="text-sm">{msg.content}</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground/60 mt-0.5 px-1">{format(new Date(msg.timestamp), "HH:mm")}</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-start">
                      <div className="max-w-full w-full">
                        {msg.result && msg.result.result_type !== "text" ? (
                          <AiResultCards result={msg.result} onAction={!loading ? handleAction : undefined} onClose={() => onOpenChange(false)} />
                        ) : (
                          <div className="rounded-2xl rounded-bl-md bg-muted px-4 py-2 max-w-[85%]">
                            <div className="text-sm prose prose-sm max-w-none text-foreground">
                              <ReactMarkdown>{msg.content}</ReactMarkdown>
                            </div>
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground/60 mt-0.5 px-1">{format(new Date(msg.timestamp), "HH:mm")}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Loading — typing indicator */}
          {loading && (
            <div className="flex justify-start mt-2">
              <div className="rounded-2xl rounded-bl-md bg-muted px-4 py-3 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:0ms]" />
                <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:150ms]" />
                <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">{error}</div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="border-t border-border bg-card px-4 py-3 flex gap-2">
          <Input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
            data-tour="ai-assistant-input"
            placeholder="Ask about your fleet..." disabled={loading} className="flex-1" />
          <Button type="submit" size="icon" disabled={loading || !query.trim()} data-tour="ai-assistant-send">
            <Send className="h-4 w-4" />
          </Button>
        </form>
        </>
        )}
      </SheetContent>
    </Sheet>
  );
}
